// What a multi may be made of, and what its chance is allowed to claim.
//
// Two rules live here. The old one is structural: no two legs off the same
// match, because their results are the same coin read twice. The new ones are
// about arithmetic — a multi is priced at ONE book (`assertOneBook`) and its
// probability is shrunk before it is multiplied (`shrinkToward`).

/** Two legs are from the same match. */
export function sameMatch(a, b) {
  return a.matchKey === b.matchKey;
}

/** Throws if any two legs share a matchKey — a multi can't combine both sides of one match. */
export function assertIndependent(legs) {
  for (let i = 0; i < legs.length; i++) {
    for (let j = i + 1; j < legs.length; j++) {
      if (sameMatch(legs[i], legs[j])) {
        throw new Error(`legs share a match: ${legs[i].matchKey}`);
      }
    }
  }
}

/**
 * THE TICKET IS PLACEABLE: every leg is at the one book the ticket names, and
 * every one of them cleared that book's own screen.
 *
 * Engine review #4 and #11. A multi built from the best price on each leg is a
 * combined price that exists at no counter in the country — three books' best
 * quotes cannot be folded into one bet slip — and the Whelan ladder stacked on
 * top of it was reading probabilities off prices nobody could take. So a multi
 * strategy picks ONE book and re-prices every leg there (`best-book` in
 * strategy.js), and this is the printer's own check that it did.
 *
 * It throws, like `assertIndependent` beside it: a ticket at the wrong book is
 * not a worse ticket, it is a ticket that cannot be placed, and a silent
 * fallback would put one in front of a punter with a book's name on it.
 */
export function assertOneBook(legs, bookmaker) {
  for (const leg of legs) {
    const where = leg.key ?? leg.matchKey ?? 'leg';
    if (leg.bookmaker !== bookmaker) {
      throw new Error(`leg is not at ${bookmaker}: ${where} is at ${leg.bookmaker}`);
    }
    if (leg.active === false) {
      throw new Error(`leg is not active at ${bookmaker}: ${where}`);
    }
  }
}

/**
 * HOW MUCH OF THE MODEL'S CLAIM SURVIVES BEING MULTIPLIED (engine review #10).
 *
 * A multi's chance is a product, and so is the error in it. Every leg on the
 * ticket was read off the same board, stripped of its margin by the same devig,
 * and (for a model strategy) predicted by the same fitted state — so a leg's
 * error is not a fresh coin toss that averages out against the next leg's, it
 * is the SAME bias arriving n times. Two points of over-claim on one leg is two
 * points; two points on each of five legs compounds to roughly ten, and the
 * multi's stated chance is wrong by more than any single leg on it could be.
 *
 * The honest response is to believe less of the model the more legs are being
 * stacked — shrink each leg's `pModel` toward its `pFair`, which is the market's
 * own answer and the one number on the ticket nobody had to estimate:
 *
 *   pUsed = pFair + (pModel − pFair) × s(n)
 *
 * `s` is a table, not a fitted curve, and it is deliberately blunt: there is no
 * data in this app that could fit a shrinkage schedule, and a formula would
 * claim there is. A single keeps the whole model (s = 1); by five legs only
 * 40% of the claimed edge is left, which is about where a 2pp per-leg bias
 * stops being recoverable. A no-model strategy is untouched by construction —
 * its `pModel` IS its `pFair`, so the shrink has nothing to move.
 */
export const SHRINK_BY_LEGS = {
  1: 1,
  2: 0.8,
  3: 0.65,
  4: 0.5,
};

/** The floor the table falls to at five legs and never leaves. */
export const SHRINK_FLOOR = 0.4;

/** How much of a leg's claimed edge survives on an n-leg ticket. */
export function shrinkFactor(legs) {
  if (!Number.isInteger(legs) || legs < 1) return SHRINK_FLOOR;
  return SHRINK_BY_LEGS[legs] ?? SHRINK_FLOOR;
}

/** One leg's probability as an n-leg ticket is allowed to use it. */
export function shrinkToward(pModel, pFair, legs) {
  if (!Number.isFinite(pModel)) return pModel;
  if (!Number.isFinite(pFair)) return pModel;
  return pFair + (pModel - pFair) * shrinkFactor(legs);
}

/**
 * Combine independent legs into one multi. `leg.p` is whatever probability
 * the caller wants combined — model or fair — this module doesn't choose.
 */
export function combineLegs(legs) {
  assertIndependent(legs);
  const price = Math.round(legs.reduce((acc, l) => acc * l.price, 1) * 100) / 100;
  const p = legs.reduce((acc, l) => acc * l.p, 1);
  return { price, p, legs: legs.length };
}
